import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ChevronLeft, Fuel } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { vehicleAPI } from '../api/index';
import { Button } from '../components/Button';
import AddVehicleModal from '../components/modals/AddVehicleModal';
import { formatDate } from '../lib/utils';

export function VehicleDetailPage() {
  const { customerId, vehicleId } = useParams<{ customerId: string; vehicleId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showEditModal, setShowEditModal] = useState(false)

  const { data: vehicles, isLoading, error } = useQuery({
    queryKey: ['customerVehicles', customerId],
    queryFn: () => vehicleAPI.listForCustomer(customerId!).then(r => r.data.vehicles),
    enabled: !!customerId,
  })

  const vehicle = vehicles?.find(v => v.id === vehicleId)

  if (error) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-500" />
          <p className="text-gray-600">Failed to load vehicle</p>
          <Button onClick={() => navigate(`/customers/${customerId}`)} className="mt-4">Back</Button>
        </div>
      </div>
    )
  }

  if (isLoading || !vehicles) {
    return (
      <div className="space-y-4">
        <div className="h-16 bg-gray-200 rounded-lg animate-pulse" />
        <div className="h-32 bg-gray-200 rounded-lg animate-pulse" />
      </div>
    )
  }

  if (!vehicle) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-500" />
          <p className="text-gray-600">Vehicle not found</p>
          <Button onClick={() => navigate(`/customers/${customerId}`)} className="mt-4">Back</Button>
        </div>
      </div>
    )
  }

  const getComplianceColor = (score: number) => {
    if (score >= 4) return '#15803d'
    if (score >= 3) return '#eab308'
    return '#dc2626'
  }

  const getComplianceBg = (score: number) => {
    if (score >= 4) return 'bg-green-50'
    if (score >= 3) return 'bg-amber-50'
    return 'bg-red-50'
  }

  const formatVehicleType = (type?: string | null) => {
    if (!type) return 'Not set'

    const labels: Record<string, string> = {
      two_wheeler: '2 Wheeler',
      three_wheeler: '3 Wheeler',
      four_wheeler: '4 Wheeler',
      commercial: 'Commercial',
      other: 'Other',
    }

    return labels[type] || type.replace(/_/g, ' ')
  }

  const formatFuelType = (fuel?: string | null) => {
    if (!fuel) return 'Not set'

    const labels: Record<string, string> = {
      petrol: 'Petrol',
      diesel: 'Diesel',
      electric: 'Electric',
      cng: 'CNG',
      hybrid: 'Hybrid',
      lpg: 'LPG',
    }

    return labels[fuel] || fuel.toUpperCase()
  }

  return (
    <div>
      <button onClick={() => navigate(`/customers/${customerId}`)} className="flex items-center gap-2 text-saffron mb-6 hover:text-orange-600">
        <ChevronLeft className="w-4 h-4" />
        Back to Customer
      </button>

      {/* Vehicle Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{vehicle.registration_number}</h1>
            <p className="text-gray-600 mt-2">{vehicle.make} {vehicle.model} {vehicle.year}</p>
            <div className="flex gap-2 mt-3">
              <span className="text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                {formatVehicleType(vehicle.vehicle_type)}
              </span>
              <span className="text-xs bg-orange-100 text-orange-800 px-3 py-1 rounded-full">
                <Fuel className="inline w-3 h-3 mr-1" />
                {formatFuelType(vehicle.fuel_type)}
              </span>
            </div>
          </div>
          <Button className="bg-saffron hover:bg-orange-600" onClick={() => setShowEditModal(true)}>
            Edit
          </Button>
        </div>
      </div>

      {/* Compliance Score Ring */}
      <div className={`${getComplianceBg((vehicle as any).compliance_score || 0)} rounded-lg border border-gray-200 p-6 mb-6`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-700 font-medium">Compliance Score</p>
            <p className="text-sm text-gray-600 mt-1">Overall vehicle compliance status</p>
          </div>
          <svg viewBox="0 0 100 100" className="w-24 h-24">
            <circle cx="50" cy="50" r="45" fill="none" stroke="#e5e7eb" strokeWidth="6" />
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke={getComplianceColor((vehicle as any).compliance_score || 0)}
              strokeWidth="6"
              strokeDasharray={`${Math.PI * 90 * ((vehicle as any).compliance_score || 0) / 5}`}
              strokeDashoffset="0"
              strokeLinecap="round"
              transform="rotate(-90 50 50)"
            />
            <text x="50" y="55" textAnchor="middle" fontSize="24" fill={getComplianceColor((vehicle as any).compliance_score || 0)} fontWeight="bold">
              {(vehicle as any).compliance_score}/5
            </text>
          </svg>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {[
          { name: 'Insurance', valid: (vehicle as any).insurance_valid, expiry: (vehicle as any).insurance_expiry, icon: '🛡️' },
          { name: 'PUC', valid: (vehicle as any).puc_valid, expiry: (vehicle as any).puc_expiry, icon: '♻️' },
          { name: 'Fitness', valid: (vehicle as any).fitness_valid, expiry: (vehicle as any).fitness_expiry, icon: '✓' },
          { name: 'Road Tax', valid: (vehicle as any).road_tax_valid, expiry: (vehicle as any).road_tax_expiry, icon: '🚗' },
          { name: 'Env Tax', valid: (vehicle as any).env_tax_valid, expiry: (vehicle as any).env_tax_expiry, icon: '🌱' },
        ].map(doc => (
          <div key={doc.name} className={`rounded-lg border p-4 ${doc.valid ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
            <div className="text-2xl mb-2">{doc.icon}</div>
            <p className="font-medium text-gray-900">{doc.name}</p>
            <p className={`text-sm mt-2 ${doc.valid ? 'text-green-700' : 'text-red-700'}`}>
              {doc.valid ? 'Valid' : 'Expired'}
            </p>
            {doc.expiry && (
              <p className="text-xs text-gray-600 mt-1">{formatDate(doc.expiry)}</p>
            )}
            <Button size="sm" variant="secondary" className="w-full mt-2">Renew</Button>
          </div>
        ))}
      </div>

      <AddVehicleModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        customerId={customerId!}
        vehicle={vehicle}
        onSuccess={() => {
          setShowEditModal(false)
          queryClient.invalidateQueries({ queryKey: ['customerVehicles', customerId] })
          queryClient.invalidateQueries({ queryKey: ['vehicles', customerId] })
          queryClient.invalidateQueries({ queryKey: ['vehicles', customerId, vehicleId] })
        }}
      />
    </div>
  )
}
